import { useEffect, useState } from 'react';

/**
 * Server-driven Terms of Service version (shop-svc USER_AGREEMENTS_IMPLEMENTATION_PLAN.md, section 10).
 *
 * shop-svc's public GET /api/v1/config says which ToS version to show, record and check
 * (`termsOfService.currentVersion`) and where its text lives (`documentUrl`, e.g. /terms/1.2). So a ToS
 * update is a shop-svc config change plus a new terms page here, never a hard-coded constant bump in
 * several clients (the 2026-06-12 sign-up outage).
 */
export interface TosConfig {
  currentVersion: string;
  minimumVersion: string;
  documentUrl: string;
}

/**
 * Used only when the config can't be fetched or predates `termsOfService`. It must always be a
 * version shop-svc still accepts (>= its minimum), which holds for the version this build shipped with.
 */
export const BUILT_IN_TOS_VERSION = '1.1';

export const BUILT_IN_TOS_CONFIG: TosConfig = {
  currentVersion: BUILT_IN_TOS_VERSION,
  minimumVersion: BUILT_IN_TOS_VERSION,
  documentUrl: `/terms/${BUILT_IN_TOS_VERSION}`,
};

const SHOP_API_BASE_URL = process.env.REACT_APP_SHOP_API_BASE_URL || 'https://shop.dev.carouselshopai.com';

/** The ToS config from a /api/v1/config body, or the built-in fallback if it's missing or malformed. */
export const tosConfigFrom = (body: unknown): TosConfig => {
  const tos = (body as { termsOfService?: Partial<TosConfig> } | null)?.termsOfService;
  if (!tos || typeof tos.currentVersion !== 'string' || !tos.currentVersion) {
    return BUILT_IN_TOS_CONFIG;
  }
  return {
    currentVersion: tos.currentVersion,
    minimumVersion: typeof tos.minimumVersion === 'string' && tos.minimumVersion
      ? tos.minimumVersion : tos.currentVersion,
    documentUrl: typeof tos.documentUrl === 'string' && tos.documentUrl
      ? tos.documentUrl : `/terms/${tos.currentVersion}`,
  };
};

let cached: Promise<TosConfig> | null = null;

/**
 * The ToS config, fetched once per page load and shared. Never rejects: a failed fetch resolves to
 * the built-in config (and is retried on the next call), so sign-up and the login gate never block on it.
 *
 * `fresh: true` refetches and replaces the shared copy. The login gate and the acceptance prompt use it:
 * a tab left open across a ToS bump otherwise kept the old version for the whole page load, so a user
 * was prompted for, linked to and recorded the PREVIOUS version, then prompted again after a reload
 * (found in Dev publishing ToS 1.3, 2026-10-07).
 */
export const getTosConfig = ({ fresh = false }: { fresh?: boolean } = {}): Promise<TosConfig> => {
  if (fresh) {
    cached = null;
  }
  if (!cached) {
    cached = fetch(`${SHOP_API_BASE_URL}/api/v1/config`)
      .then(async response => {
        if (!response.ok) throw new Error(`config ${response.status}`);
        return tosConfigFrom(await response.json());
      })
      .catch(err => {
        console.error('Failed to load Terms of Service config; using the built-in version:', err);
        cached = null;
        return BUILT_IN_TOS_CONFIG;
      });
  }
  return cached;
};

/** For rendering (links, labels): the built-in config until the fetched one arrives. */
export const useTosConfig = (): TosConfig => {
  const [config, setConfig] = useState<TosConfig>(BUILT_IN_TOS_CONFIG);
  useEffect(() => {
    let cancelled = false;
    getTosConfig().then(c => { if (!cancelled) setConfig(c); });
    return () => { cancelled = true; };
  }, []);
  return config;
};

/**
 * For the acceptance prompt: refetches on mount and returns null until the CURRENT config arrives, so
 * the prompt never links to (or lets the user accept) a version that is not current. The caller shows
 * a neutral link meanwhile (/terms always renders the current version).
 */
export const useFreshTosConfig = (): TosConfig | null => {
  const [config, setConfig] = useState<TosConfig | null>(null);
  useEffect(() => {
    let cancelled = false;
    getTosConfig({ fresh: true }).then(c => { if (!cancelled) setConfig(c); });
    return () => { cancelled = true; };
  }, []);
  return config;
};

/**
 * Which terms document a /terms URL should show: the requested version if this build has its text,
 * else (for plain /terms) the current version, else the newest this build has. Returns null for a
 * requested version this build doesn't have, so the page can say so instead of showing other text.
 */
export const resolveTermsVersion = (
  requested: string | undefined,
  current: string,
  available: string[]
): string | null => {
  if (requested) {
    return available.includes(requested) ? requested : null;
  }
  if (available.includes(current)) return current;
  return available[available.length - 1] ?? null;
};
